// A chart, drawn from its ```chart block (shared/src/charts.ts), in a post or
// in a chapter's commentary.
//
// The author's own numbers are horizontal bars, each labelled at its tip, laid
// out as a table so the chart is also its own table view. Spans of years are a
// timeline: the same table, each row's bar placed on one shared axis of years,
// with the dates as written beside it. A word counted through the text is
// columns in canonical order — the order is the point — on round ticks, the
// tallest labelled, a readout under the pointer, and every count listed
// beneath for anyone not pointing.
//
// Every chart here is one series, so one hue: the accent, which clears the
// palette checks against both the light and the dark paper. Text stays in the
// ink tokens; only the marks wear the accent.
import { bookById, parseRef, toDisplay, type ChartSpec, type WordUsageView } from "@bibleunleashed/shared";
import { useState, type ReactNode } from "react";
import { useWordUsage } from "@/lib/study";
import { divisions, niceTicks, yearLabel, yearTicks } from "./scale";

type BarsSpec = Extract<ChartSpec, { kind: "bars" }>;
type TimelineSpec = Extract<ChartSpec, { kind: "timeline" }>;
type UsageSpec = Extract<ChartSpec, { kind: "usage" }>;

const format = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format;

export function Chart({ chart }: { chart: ChartSpec }) {
  switch (chart.kind) {
    case "bars":
      return <Bars chart={chart} />;
    case "timeline":
      return <Timeline chart={chart} />;
    case "usage":
      return <Usage chart={chart} />;
  }
}

/** Only ever seen while writing: a post or a chapter holding one cannot be saved. */
export function BadChart({ error }: { error: string }) {
  return (
    <p className="my-6 rounded-md border border-rule px-4 py-3 font-sans text-[13px] text-ink-2">
      This chart can’t be drawn: {error}
    </p>
  );
}

function Figure({ title, subtitle, children, notes }: { title: string; subtitle?: ReactNode; children: ReactNode; notes: ReactNode[] }) {
  return (
    <figure className="my-10 font-sans">
      <figcaption className="mb-4">
        <span className="block text-[15px] leading-snug font-semibold text-ink">{title}</span>
        {subtitle && <span className="mt-1 block text-[13px] leading-snug text-ink-2">{subtitle}</span>}
      </figcaption>
      {children}
      {notes.filter(Boolean).map((note, i) => (
        <p key={i} className="mt-2 text-[12px] leading-snug text-ink-3">
          {note}
        </p>
      ))}
    </figure>
  );
}

function Bars({ chart }: { chart: BarsSpec }) {
  const max = Math.max(...chart.rows.map((r) => r.value));
  // The unit rides on the first number only, as a newspaper table has it.
  const values = chart.rows.map((r, i) => (i === 0 && chart.unit ? `${format(r.value)} ${chart.unit}` : format(r.value)));
  // Room past the longest bar for its number, so no label is pushed out of the frame.
  const reserve = Math.max(...values.map((v) => v.length)) + 1;
  const width = (value: number) =>
    max > 0 && value > 0 ? `max(2px, calc((100% - ${reserve}ch) * ${value / max}))` : "0px";

  return (
    <Figure title={chart.title} notes={[chart.note]}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead className="sr-only">
            <tr>
              <th scope="col">Item</th>
              <th scope="col">{chart.unit || "Value"}</th>
            </tr>
          </thead>
          <tbody>
            {chart.rows.map((row, i) => (
              <tr key={i} className="group">
                <th scope="row" className="w-px py-[3px] pr-3 text-left font-normal whitespace-nowrap text-ink-2">
                  {row.label}
                </th>
                <td className="py-[3px]">
                  <div className="flex items-center gap-1.5">
                    <div
                      className="h-3.5 shrink-0 rounded-r-[4px] bg-accent transition-opacity group-hover:opacity-75"
                      style={{ width: width(row.value) }}
                    />
                    <span className="whitespace-nowrap text-ink-2 tabular-nums">{values[i]}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Figure>
  );
}

function Timeline({ chart }: { chart: TimelineSpec }) {
  const ticks = yearTicks(Math.min(...chart.rows.map((r) => r.start)), Math.max(...chart.rows.map((r) => r.end)));
  const lo = ticks[0]!;
  const hi = ticks.at(-1)!;
  const x = (year: number) => ((year - lo) / (hi - lo)) * 100;
  const last = ticks.length - 1;
  // The end labels sit inside the axis rather than hanging off it.
  const shift = (i: number) => (i === 0 ? "0%" : i === last ? "-100%" : "-50%");
  const grid = ticks.map((t) => (
    <span key={t} className="absolute inset-y-0 border-l border-rule" style={{ left: `${x(t)}%` }} />
  ));

  return (
    <Figure title={chart.title} notes={[chart.note]}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead className="sr-only">
            <tr>
              <th scope="col">Who or what</th>
              <th scope="col">On the timeline</th>
              <th scope="col">When</th>
            </tr>
          </thead>
          <tbody>
            {chart.rows.map((row, i) => (
              <tr key={i} className="group">
                <th scope="row" className="w-px pr-3 text-left font-normal whitespace-nowrap text-ink-2">
                  {row.label}
                </th>
                <td className="p-0">
                  <div className="relative h-[22px] min-w-40">
                    {grid}
                    {row.start === row.end ? (
                      <span
                        className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent ring-2 ring-paper"
                        style={{ left: `${x(row.start)}%` }}
                      />
                    ) : (
                      <span
                        className="absolute top-1/2 h-3.5 -translate-y-1/2 rounded-[4px] bg-accent transition-opacity group-hover:opacity-75"
                        style={{ left: `${x(row.start)}%`, width: `max(2px, ${x(row.end) - x(row.start)}%)` }}
                      />
                    )}
                  </div>
                </td>
                <td className="w-px pl-3 text-right whitespace-nowrap text-ink-2 tabular-nums">{row.when}</td>
              </tr>
            ))}
            <tr aria-hidden="true">
              <td />
              <td className="p-0">
                <div className="relative h-6 border-t border-ink-3">
                  {ticks.map((t, i) => (
                    <span
                      key={t}
                      // Every other year on a phone, so the labels do not run together.
                      className={`absolute top-1 text-[11px] whitespace-nowrap text-ink-3 tabular-nums ${i % 2 ? "hidden sm:inline" : ""}`}
                      style={{ left: `${x(t)}%`, transform: `translateX(${shift(i)})` }}
                    >
                      {yearLabel(t)}
                    </span>
                  ))}
                </div>
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </Figure>
  );
}

const PLOT = "h-48";

function Usage({ chart }: { chart: UsageSpec }) {
  const { data, isLoading, error } = useWordUsage(chart.word, chart.book);
  const book = chart.book ? bookById(chart.book) : undefined;
  const testament = chart.word.startsWith("H") ? "Old Testament" : "New Testament";
  const where = book ? `in ${book.name}, chapter by chapter` : `in the ${testament}, book by book`;

  const subtitle = data ? (
    <>
      <bdi lang={data.language === "greek" ? "grc" : "he"} className="text-ink">
        {data.lemma}
      </bdi>{" "}
      {data.transliteration} ({data.id}): {format(data.total)} {data.total === 1 ? "time" : "times"} {where}
    </>
  ) : (
    `${chart.word} ${where}`
  );

  return (
    <Figure title={chart.title} subtitle={subtitle} notes={[chart.note, data?.notice]}>
      {isLoading && <div className={`${PLOT} mt-5 grid place-items-center text-[13px] text-ink-3`}>Counting…</div>}
      {error && (
        <p className="text-[13px] text-ink-2">
          Couldn’t count {chart.word}: {error.message}
        </p>
      )}
      {data && <Columns view={data} />}
    </Figure>
  );
}

/** "Psalms" for a book, "Psalm 23" for a chapter. */
const nameOf = (osis: string): string => (osis.includes(".") ? toDisplay(parseRef(osis)) : (bookById(osis)?.name ?? osis));

function Columns({ view }: { view: WordUsageView }) {
  const [hover, setHover] = useState<number | null>(null);
  const { counts } = view;
  const n = counts.length;
  const max = Math.max(...counts.map((c) => c.count));
  const ticks = niceTicks(max);
  const top = ticks.at(-1)!;
  const peak = counts.findIndex((c) => c.count === max);
  // A 2px gap between columns, narrowing on a small screen so 150 psalms still have width.
  const grid = { gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, columnGap: `min(2px, ${40 / n}%)` };
  const centre = (i: number) => `${((i + 0.5) / n) * 100}%`;
  // Keep the readout inside the frame at either end.
  const shift = (i: number) => (i / n < 0.15 ? "-10%" : i / n > 0.85 ? "-90%" : "-50%");
  const testament = view.id.startsWith("H") ? "OT" : "NT";
  // Chapter labels far enough apart that three digits do not run together on a phone.
  const step = n <= 15 ? 1 : n <= 60 ? 5 : 25;

  return (
    <div>
      <div className={`relative mt-6 ml-9 ${PLOT}`} onPointerLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <div
            key={t}
            className={`absolute inset-x-0 border-t ${t === 0 ? "border-ink-3" : "border-rule"}`}
            style={{ bottom: `${(t / top) * 100}%` }}
          >
            <span className="absolute top-0 right-full mr-2 -translate-y-1/2 text-[11px] text-ink-3 tabular-nums">
              {format(t)}
            </span>
          </div>
        ))}

        <div className="absolute inset-0 grid items-end" style={grid}>
          {counts.map((c, i) => (
            <div key={c.osis} className="flex h-full items-end justify-center" onPointerEnter={() => setHover(i)}>
              <div
                className={`w-full max-w-6 rounded-t-[4px] bg-accent transition-opacity ${hover === i ? "opacity-75" : ""}`}
                style={{ height: `${(c.count / top) * 100}%` }}
              />
            </div>
          ))}
        </div>

        {max > 0 && hover === null && (
          <span
            className="pointer-events-none absolute text-[11.5px] font-semibold whitespace-nowrap text-ink tabular-nums"
            style={{ left: centre(peak), bottom: `calc(${(max / top) * 100}% + 3px)`, transform: `translateX(${shift(peak)})` }}
          >
            {format(max)} <span className="font-normal text-ink-2">{nameOf(counts[peak]!.osis)}</span>
          </span>
        )}

        {hover !== null && (
          <div
            className="pointer-events-none absolute bottom-full z-10 mb-1 rounded-md border border-rule bg-paper px-2.5 py-1.5 text-[12px] whitespace-nowrap shadow-pop"
            style={{ left: centre(hover), transform: `translateX(${shift(hover)})` }}
          >
            <span className="font-semibold text-ink tabular-nums">{format(counts[hover]!.count)}</span>{" "}
            <span className="text-ink-2">{nameOf(counts[hover]!.osis)}</span>
          </div>
        )}
      </div>

      <div className="mt-1.5 ml-9 grid text-[11px] leading-tight text-ink-3" style={grid}>
        {view.book
          ? counts.map((c, i) => (
              <span key={c.osis} className="flex justify-center">
                {(i === 0 || (i + 1) % step === 0) && <span className="whitespace-nowrap tabular-nums">{i + 1}</span>}
              </span>
            ))
          : divisions(testament).map((d) => (
              <span key={d.name} className="border-t border-rule pt-1 text-center" style={{ gridColumn: `span ${d.span}` }}>
                {d.name}
              </span>
            ))}
      </div>

      <details className="mt-4 text-[12.5px]">
        <summary className="cursor-pointer text-ink-3 hover:text-ink">
          Every {view.book ? "chapter" : "book"}, counted
        </summary>
        <ol className="mt-2 columns-2 gap-8 sm:columns-3">
          {counts.map((c) => (
            <li key={c.osis} className="flex break-inside-avoid justify-between gap-3 border-b border-rule py-0.5">
              <span className="text-ink-2">{nameOf(c.osis)}</span>
              <span className="text-ink tabular-nums">{format(c.count)}</span>
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}
